// edit-variant.component.ts
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { VariantService } from 'app/services/variant/variant.service';

@Component({
  selector: 'app-edit-variant',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './edit-variant.component.html',
  styleUrls: ['./edit-variant.component.scss']
})
export class EditVariantComponent implements OnInit {
  variantForm!: FormGroup;
  submitted = false;
  isLoading = true;
  loadingMessage = 'جاري تحميل بيانات المتغير...';
  currentVariantCode: string = "";
  currentVariantId: number = 0;
  isUpdating = false;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private variantService : VariantService
  ) {}

  ngOnInit() {
    this.initializeForm();
    this.loadVariantData();
  }

  private initializeForm() {
    this.variantForm = this.fb.group({
      code: ['', [
        Validators.required, 
        Validators.minLength(2), 
        Validators.maxLength(20),
        Validators.pattern('^[0-9]+$')
      ]],
      name: ['', [
        Validators.required, 
        Validators.minLength(2),
        Validators.maxLength(100)
      ]],
      measurement: ['', [Validators.required]],
      status: [true, [Validators.required]]
    });
  }

  private loadVariantData(): void {
    const variantCode = this.route.snapshot.paramMap.get('code');
    alert(variantCode)
    if (!variantCode || isNaN(+variantCode)) {
      console.error('❌ Invalid variant Code:', variantCode);
      alert('❌ معرّف المتغير غير صالح');
      this.router.navigate(['/admin/variants']);
      return;
    }

    this.currentVariantCode = variantCode;
    console.log('🟡 Loading variant with Code:', this.currentVariantCode);

    this.variantService.getVariantByCode(this.currentVariantCode).subscribe({
      next: (response) => {
        console.log('✅ Variant data loaded:', response);
        
        if (response.success) {
          this.currentVariantId = response.id;
          const variant = response;
          this.patchVariantData(variant);
          this.isLoading = false;
        } else {
          console.error('❌ No variant data found');
          alert('❌ لم يتم العثور على بيانات المتغير');
          this.router.navigate(['/admin/variants']);
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error loading variant:', error);
        this.isLoading = false;
        this.handleLoadError(error);
      }
    });

    // Temporary mock data for testing
    // setTimeout(() => {
    //   const mockVariant = {
    //     id: 1,
    //     code: '123',
    //     name: 'متغير تجريبي',
    //     measurement: 'KG',
    //     status: true
    //   };
    //   this.patchVariantData(mockVariant);
    //   this.isLoading = false;
    // }, 1000);
  }

  private patchVariantData(variant: any): void {
    console.log('🟡 Patching variant data:', variant);
    
    this.variantForm.patchValue({
      code: variant.code || '',
      name: variant.name || '',
      measurement: variant.measurement || '',
      status: variant.status !== undefined ? variant.status : true
    });

    console.log('✅ Variant data patched successfully');
  }

  // الدوال المساعدة للوصول إلى عناصر الفورم
  get f(): { [key: string]: AbstractControl } {
    return this.variantForm.controls;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.variantForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.variantForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        return 'هذا الحقل مطلوب';
      }
      if (field.errors['minlength']) {
        return `الحد الأدنى للحروف هو ${field.errors['minlength'].requiredLength}`;
      }
      if (field.errors['maxlength']) {
        return `الحد الأقصى للحروف هو ${field.errors['maxlength'].requiredLength}`;
      }
      if (field.errors['pattern']) {
        return 'يجب أن يحتوي الكود على أرقام فقط';
      }
    }
    return '';
  }

  // تحديث المتغير
  onSubmit(): void {
    this.submitted = true;

    if (!this.variantForm.valid) {
      this.markFormGroupTouched(this.variantForm);
      alert('❌ يوجد أخطاء في النموذج. راجع الحقول المطلوبة.');
      return;
    }

    if (!this.hasFormChanged()) {
      alert('⚠️ لم تقم بإجراء أي تغييرات على البيانات');
      return;
    }

    this.updateVariant();
  }

  private hasFormChanged(): boolean {
    return this.variantForm.dirty;
  }

  private updateVariant(): void {
    this.isUpdating = true;
    this.loadingMessage = 'جاري تحديث المتغير...';

    const variantData = this.prepareVariantData();

    console.log('🟡 Updating variant with data:', variantData);

    this.variantService.UpdateVariant(this.currentVariantId,variantData).subscribe({
      next: (response) => {
        console.log('✅ Variant updated successfully:', response);
        this.isUpdating = false;
        
        if (response.success) {
          alert('✅ تم تحديث المتغير بنجاح!');
          this.router.navigate(['/admin/variants']);
        } else {
          alert('❌ ' + (response.message || 'حدث خطأ أثناء تحديث المتغير'));
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error updating variant:', error);
        this.isUpdating = false;
        this.handleError(error);
      }
    });

    // Temporary mock success
    // setTimeout(() => {
    //   console.log('✅ Variant updated successfully (mock)');
    //   this.isUpdating = false;
    //   alert('✅ تم تحديث المتغير بنجاح!');
    //   this.router.navigate(['/admin/variants']);
    // }, 1500);
  }

  private prepareVariantData(): any {
    const formValue = this.variantForm.value;
    
    return {
      code: formValue.code.trim(),
      name: formValue.name.trim(),
      measurement: formValue.measurement,
      status: Boolean(formValue.status)
    };
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }

  private handleLoadError(error: HttpErrorResponse): void {
    let errorMessage = '❌ حدث خطأ في تحميل بيانات المتغير';
    
    if (error.status === 404) {
      errorMessage = '❌ المتغير غير موجود';
    } else if (error.status === 0) {
      errorMessage = '❌ تعذر الاتصال بالخادم';
    }
    
    alert(errorMessage);
    this.router.navigate(['/admin/variants']);
  }

  private handleError(error: HttpErrorResponse): void {
    let errorMessage = '❌ حدث خطأ أثناء تحديث المتغير';
    
    if (error.error && error.error.errors) {
      const validationErrors = error.error.errors;
      const errorMessages = [];
      
      for (const field in validationErrors) {
        if (validationErrors.hasOwnProperty(field)) {
          errorMessages.push(...validationErrors[field]);
        }
      }
      
      if (errorMessages.length > 0) {
        errorMessage = '❌ ' + errorMessages.join('\n');
      }
    } else if (error.error && error.error.message) {
      errorMessage = '❌ ' + error.error.message;
    } else if (error.status === 404) {
      errorMessage = '❌ المتغير غير موجود';
    } else if (error.status === 409) {
      errorMessage = '❌ كود المتغير موجود مسبقاً';
    }
    
    alert(errorMessage);
  }

  onReset(): void {
    if (confirm('هل تريد إعادة تعيين التغييرات؟')) {
      this.loadVariantData();
      this.submitted = false;
    }
  }

  onCancel(): void {
    if (this.variantForm.dirty) {
      if (confirm('هل تريد إلغاء التغييرات؟ سيتم فقدان جميع التعديلات.')) {
        this.router.navigate(['/admin/variants']);
      }
    } else {
      this.router.navigate(['/admin/variants']);
    }
  }

  // دالة للتحقق من صحة الكود
  validateCode(): void {
    const codeControl = this.variantForm.get('code');
    if (codeControl?.value) {
      const code = codeControl.value;
      if (!/[0-9]/.test(code)) {
        codeControl.setErrors({ pattern: true });
      } else {
        // إزالة خطأ النمط إذا كان الكود صالحاً
        if (codeControl.errors?.['pattern']) {
          delete codeControl.errors['pattern'];
          if (Object.keys(codeControl.errors).length === 0) {
            codeControl.setErrors(null);
          }
        }
      }
    }
  }

  // دالة للتحقق إذا كان النموذج محمل
  isFormLoaded(): boolean {
    return !this.isLoading && this.currentVariantCode != "";
  }
}